import {
  CloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Slider,
  Box,
  Dialog,
  Portal,
  type SliderValueChangeDetails,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react'
import type { ScoringProfile } from '../../types/analyticsMap'
import { SCORING_PROFILE_COLORS } from '../../types/analyticsMap'

interface HighValueSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  profiles: ScoringProfile[]
  thresholds: Record<string, number>
  onApply: (thresholds: Record<string, number>) => void
}

export function HighValueSettingsModal({
  isOpen,
  onClose,
  profiles,
  thresholds,
  onApply,
}: HighValueSettingsModalProps) {
  const [draft, setDraft] = useState<Record<string, number>>(thresholds)

  useEffect(() => {
    if (isOpen) setDraft(thresholds)
  }, [isOpen, thresholds])

  const handleChange = (profileName: string, value: number) => {
    setDraft((prev) => ({ ...prev, [profileName]: value }))
  }

  const handleApply = () => {
    onApply(draft)
    onClose()
  }

  return (
    <Dialog.Root open={isOpen} size='md' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header fontSize="md">Score Thresholds</Dialog.Header>
            <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
            <Dialog.Body>
              <Text fontSize="sm" color="gray.600" mb={4}>
                Set the minimum score (1-100) a contact must reach to be highlighted for each profile.
              </Text>
              <VStack gap={4} align="stretch">
                {profiles.map((profile) => {
                  const color = SCORING_PROFILE_COLORS[profile.name] ?? '#9CA3AF'
                  const value = draft[profile.name] ?? profile.defaultThreshold
                  return (
                    <Box key={profile.name}>
                      <HStack justify="space-between" mb={1}>
                        <HStack gap={2}>
                          <Box w={3} h={3} borderRadius="full" bg={color} />
                          <Text fontSize="sm" fontWeight="medium">
                            {profile.label}
                          </Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.500" fontWeight="semibold">
                          {value}
                        </Text>
                      </HStack>
                      <Slider.Root
                        value={[value]}
                        min={1}
                        max={100}
                        step={1}
                        onValueChange={(details: SliderValueChangeDetails) => handleChange(profile.name, details.value[0])}
                      >
                        <Slider.Control>
                          <Slider.Track>
                            <Slider.Range bg={color} />
                          </Slider.Track>
                          <Slider.Thumb index={0}>
                            <Slider.HiddenInput />
                          </Slider.Thumb>
                        </Slider.Control>
                      </Slider.Root>
                    </Box>
                  );
                })}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button size="sm" variant="ghost" mr={2} onClick={onClose}>
                Cancel
              </Button>
              <Button size="sm" colorPalette="brand" onClick={handleApply}>
                Apply
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
